import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Checkbox, SelectInput, TextInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { AUSWERTUNGEN_SECTION_SUBTITLES } from "@/lib/auswertungenNav";
import {
  currentYear,
  fetchHeroUsageReport,
  filterHeroUsageRows,
  formatDurationHours,
  getAssignmentState,
  getCachedHeroUsageReport,
  invalidateHeroUsageReportCache,
  shiftYear,
  yearDateRange,
  type HeroUsageStatusFilter,
} from "@/lib/auswertungen";
import { useRealtime } from "@/lib/useRealtime";
import { HeroUsageRankings } from "@/pages/auswertungen/HeroUsageRankings";
import "./auswertungen.css";

const STATUS_OPTIONS: { value: HeroUsageStatusFilter; label: string }[] = [
  { value: "all", label: "Alle" },
  { value: "assigned", label: "Stundennachweis zugeordnet" },
  { value: "unassigned", label: "kein Stundennachweis" },
  { value: "unmapped", label: "kein Stundennachweis (kein Projektmapping)" },
  { value: "festpreis", label: "Festpreis" },
  { value: "auf_nachweis", label: "Auf Nachweis" },
  { value: "ohne_berechnung", label: "Ohne Berechnung" },
  { value: "gewaehrleistung", label: "Gewährleistung" },
];

const SUMMARY_TILES: {
  key: string;
  label: string;
  getSeconds: (totals: ReturnType<typeof filterHeroUsageRows>["totals"]) => number;
  className: string;
}[] = [
  {
    key: "unassigned",
    label: "Ohne Nachweis",
    getSeconds: (totals) => totals.unassignedSeconds,
    className: "eh-open-hours-card--ohne_nachweis",
  },
  {
    key: "assigned",
    label: "Mit Nachweis",
    getSeconds: (totals) => totals.assignedSeconds,
    className: "eh-open-hours-card--mit_nachweis",
  },
  {
    key: "festpreis",
    label: "Festpreis",
    getSeconds: (totals) => totals.festpreisSeconds,
    className: "eh-open-hours-card--festpreis",
  },
  {
    key: "auf_nachweis",
    label: "Auf Nachweis",
    getSeconds: (totals) => totals.aufNachweisSeconds,
    className: "eh-open-hours-card--auf_nachweis",
  },
  {
    key: "ohne_berechnung",
    label: "Ohne Berechnung",
    getSeconds: (totals) => totals.ohneBerechnungSeconds,
    className: "eh-open-hours-card--ohne_berechnung",
  },
  {
    key: "gewaehrleistung",
    label: "Gewährleistung",
    getSeconds: (totals) => totals.gewaehrleistungSeconds,
    className: "eh-open-hours-card--gewaehrleistung",
  },
];

export function HeroUsageRankingsPage() {
  const initialYear = useMemo(currentYear, []);
  const initialRange = useMemo(() => yearDateRange(initialYear), [initialYear]);

  const [year, setYear] = useState(initialYear);
  const [person, setPerson] = useState("");
  const [status, setStatus] = useState<HeroUsageStatusFilter>("all");
  const [hideFullLicense, setHideFullLicense] = useState(true);

  const { from, to } = useMemo(() => yearDateRange(year), [year]);

  const [rows, setRows] = useState(() => getCachedHeroUsageReport(initialRange.from, initialRange.to)?.rows ?? null);
  const [loading, setLoading] = useState(rows === null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadRunRef = useRef(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const load = useCallback(
    async (opts?: { background?: boolean; force?: boolean }) => {
      const runId = ++loadRunRef.current;
      const cached = getCachedHeroUsageReport(from, to);

      if (cached) {
        setRows(cached.rows);
        setLoading(false);
        if (cached.isFresh && !opts?.force) return;
      }

      if (cached || opts?.background) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        const data = await fetchHeroUsageReport(from, to);
        if (!isMountedRef.current || runId !== loadRunRef.current) return;
        setRows(data);
        setError(null);
      } catch (err) {
        if (!isMountedRef.current || runId !== loadRunRef.current) return;
        setError(
          err instanceof ApiError
            ? err.message
            : "Rankings konnten nicht geladen werden.",
        );
      } finally {
        if (isMountedRef.current && runId === loadRunRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [from, to],
  );

  useEffect(() => {
    void load();
  }, [load]);

  useRealtime({
    scopes: ["domain:timesheets"],
    domains: ["timesheets"],
    events: ["timesheet_hero_usage_updated"],
    onChange: () => void load({ background: true, force: true }),
  });

  const { filtered, totals } = useMemo(
    () => filterHeroUsageRows(rows ?? [], { person, status, hideFullLicense }),
    [rows, person, status, hideFullLicense],
  );

  const onlyUnmapped = useMemo(() => {
    if (!rows || rows.length === 0) return false;
    return rows.every((row) => getAssignmentState(row) === "unmapped");
  }, [rows]);

  const hasPartialData = useMemo(
    () => Boolean(rows?.some((row) => row.partial_data)),
    [rows],
  );

  const initialLoading = loading && rows === null;
  const isCurrentYear = year === currentYear();

  return (
    <AppShell
      title="Auswertungen"
      subtitle={AUSWERTUNGEN_SECTION_SUBTITLES["open-hours-ranking"]}
      actions={
        <div className="flex items-center gap-2">
          {refreshing && (
            <span className="flex items-center gap-1.5 text-xs text-label-secondary">
              <Spinner className="h-3.5 w-3.5" />
              wird aktualisiert …
            </span>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void load({ force: true })}
            loading={loading || refreshing}
          >
            Neu laden
          </Button>
        </div>
      }
    >
      {error && (
        <Alert variant="error" title="Rankings konnten nicht geladen werden">
          {error}
        </Alert>
      )}
      {hasPartialData && (
        <Alert variant="warning" title="Unvollständige Daten">
          Hero konnte nicht für alle Projekte abgefragt werden. Die Auswertung ist
          möglicherweise unvollständig.
        </Alert>
      )}

      <section className="rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-separator text-lg text-label transition hover:border-accent hover:text-accent"
              aria-label="Vorheriges Jahr"
              onClick={() => setYear((current) => shiftYear(current, -1))}
            >
              ‹
            </button>
            <div className="min-w-[5rem] text-center text-base font-semibold text-label">{year}</div>
            <button
              type="button"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-separator text-lg text-label transition hover:border-accent hover:text-accent"
              aria-label="Nächstes Jahr"
              onClick={() => setYear((current) => shiftYear(current, 1))}
            >
              ›
            </button>
            {!isCurrentYear ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="h-8"
                onClick={() => setYear(currentYear())}
              >
                Aktuelles Jahr
              </Button>
            ) : null}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <TextInput
            label="Person"
            placeholder="Name oder Partner"
            value={person}
            onChange={(e) => setPerson(e.target.value)}
          />
          <SelectInput
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value as HeroUsageStatusFilter)}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </SelectInput>
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <Checkbox
            label="Hero Lizenz Full ausblenden"
            checked={hideFullLicense}
            onChange={(e) => setHideFullLicense(e.target.checked)}
          />
          <p className="text-xs text-label-secondary">
            Rankings basieren auf dem gesamten Jahr {year}. Filter wirken sofort.
          </p>
        </div>
      </section>

      {initialLoading ? (
        <div className="flex items-center justify-center py-16 text-label-secondary">
          <Spinner />
          <span className="ml-2 text-sm">Rankings werden geladen …</span>
        </div>
      ) : (
        <>
          <section className="space-y-3">
            <p className="text-sm text-label-secondary">
              <span className="font-medium text-label">
                {filtered.length ? `${filtered.length} Einträge` : "Keine Einträge"} · {year}
              </span>
            </p>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
              {SUMMARY_TILES.map((tile) => (
                <div
                  key={tile.key}
                  className={`eh-open-hours-card rounded-ios-lg px-4 py-3 shadow-ios-md ring-1 ring-separator/35 ${tile.className}`}
                >
                  <p className="text-xs font-medium text-label-secondary">{tile.label}</p>
                  <p className="mt-1 text-xl font-semibold tabular-nums text-label">
                    {formatDurationHours(tile.getSeconds(totals))}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {onlyUnmapped && (
            <p className="text-sm text-label-secondary">
              Hinweis: Es wurden nur nicht zugeordnete Hero-Zeiteinträge gefunden (kein
              lokales Projekt-Mapping).
            </p>
          )}

          <HeroUsageRankings
            rows={filtered}
            year={year}
            onEntriesUpdated={async () => {
              invalidateHeroUsageReportCache();
              await load({ force: true });
            }}
          />
        </>
      )}
    </AppShell>
  );
}
